import {AccountDisplayStatus, AccountState} from 'web-angular-client';
import {Injectable} from '@angular/core';

/**
 * Utility class for providing some user-friendly transformations to na {@link AccountState}.
 */
@Injectable({
    providedIn: 'root'
})
export class AccountStatusUtil
{

    public ANY = AccountDisplayStatus.ANY;
    public PAYMENT_OUTSTANDING = AccountDisplayStatus.PAYMENT_OUTSTANDING;
    public PROCESSING_PAYMENT = AccountDisplayStatus.PROCESSING_PAYMENT;
    public PAYMENT_COMPLETED = AccountDisplayStatus.PAYMENT_COMPLETED;
    public PARTIAL_PAYMENT_RECEIVED = AccountDisplayStatus.PARTIAL_PAYMENT_RECEIVED;
    public PAYMENT_READY_FOR_SETTLEMENT = AccountDisplayStatus.PAYMENT_READY_FOR_SETTLEMENT;
    public SETTLEMENT_PROCESSED = AccountDisplayStatus.SETTLEMENT_PROCESSED;
    public PAYMENT_CANCELLED = AccountDisplayStatus.PAYMENT_CANCELLED;
    public PAYMENT_EXPIRED = AccountDisplayStatus.PAYMENT_EXPIRED;
    public CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE = AccountDisplayStatus.CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE;

    public getSimplifiedDescription(accountState: AccountState): string
    {
        switch (accountState)
        {
            case AccountState.PAYMENT_OUTSTANDING:
                return this.PAYMENT_OUTSTANDING;
            case AccountState.PROCESSING_PAYMENT:
                return this.PROCESSING_PAYMENT;
            case AccountState.PAYMENT_COMPLETED:
            case AccountState.PAYMENT_FEES_ISSUE:
                return this.PAYMENT_COMPLETED;
            case AccountState.PARTIAL_PAYMENT_RECEIVED:
                return this.PARTIAL_PAYMENT_RECEIVED;
            case AccountState.PAYMENT_READY_FOR_SETTLEMENT:
                return this.PAYMENT_READY_FOR_SETTLEMENT;
            case AccountState.SETTLEMENT_PROCESSED:
                return this.SETTLEMENT_PROCESSED;
            case AccountState.PAYMENT_CANCELLED:
                return this.PAYMENT_CANCELLED;
            case AccountState.PAYMENT_EXPIRED:
                return this.PAYMENT_EXPIRED;
            case AccountState.CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE:
                return this.CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE;
            default:
                return 'Unknown';
        }
    }

    public getAccountStateFromDescription(shortDesc: string): Array<AccountState>
    {
        switch (shortDesc)
        {
            case this.PAYMENT_OUTSTANDING:
                return [AccountState.PAYMENT_OUTSTANDING];
            case this.PROCESSING_PAYMENT:
                return [AccountState.PROCESSING_PAYMENT];
            case this.PAYMENT_COMPLETED:
                return [AccountState.PAYMENT_COMPLETED, AccountState.PAYMENT_FEES_ISSUE];
            case this.PARTIAL_PAYMENT_RECEIVED:
                return [AccountState.PARTIAL_PAYMENT_RECEIVED];
            case this.PAYMENT_READY_FOR_SETTLEMENT:
                return [AccountState.PAYMENT_READY_FOR_SETTLEMENT];
            case this.SETTLEMENT_PROCESSED:
                return [AccountState.SETTLEMENT_PROCESSED];
            case this.PAYMENT_CANCELLED:
                return [AccountState.PAYMENT_CANCELLED];
            case this.PAYMENT_EXPIRED:
                return [AccountState.PAYMENT_EXPIRED];
            case this.CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE:
                return [AccountState.CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE];
        }
        return [];
    }

    public getDisplayStatusFromDescription(description: string): AccountDisplayStatus
    {
        switch (description)
        {
            case this.PAYMENT_OUTSTANDING:
                return AccountDisplayStatus.PAYMENT_OUTSTANDING;
            case this.PROCESSING_PAYMENT:
                return AccountDisplayStatus.PROCESSING_PAYMENT;
            case this.PAYMENT_COMPLETED:
                return AccountDisplayStatus.PAYMENT_COMPLETED;
            case this.PARTIAL_PAYMENT_RECEIVED:
                return AccountDisplayStatus.PARTIAL_PAYMENT_RECEIVED;
            case this.PAYMENT_READY_FOR_SETTLEMENT:
                return AccountDisplayStatus.PAYMENT_READY_FOR_SETTLEMENT;
            case this.SETTLEMENT_PROCESSED:
                return AccountDisplayStatus.SETTLEMENT_PROCESSED;
            case this.PAYMENT_CANCELLED:
                return AccountDisplayStatus.PAYMENT_CANCELLED;
            case this.PAYMENT_EXPIRED:
                return AccountDisplayStatus.PAYMENT_EXPIRED;
            case this.CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE:
                return AccountDisplayStatus.CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE;
            default:
                return AccountDisplayStatus.ANY;
        }
    }

    public getLongDescription(accountState: AccountState): string
    {
        switch (accountState)
        {
            case AccountState.PAYMENT_OUTSTANDING:
                return 'Customer has not yet paid.';
            case AccountState.PROCESSING_PAYMENT:
                return 'A payment is being currently processed for this request to pay transaction.';
            case AccountState.PAYMENT_COMPLETED:
                return 'Customer has paid the full amount.';
            case AccountState.PARTIAL_PAYMENT_RECEIVED:
                return 'Customer has only paid a partial amount of the total.';
            case AccountState.PAYMENT_READY_FOR_SETTLEMENT:
                return 'The request to pay transaction has been added to a settlement batch';
            case AccountState.SETTLEMENT_PROCESSED:
                return 'The request to pay transaction has been submitted for settlement in a batch.';
            case AccountState.PAYMENT_EXPIRED:
                return 'The request to pay transaction is no longer valid to accept payments.';
            case AccountState.PAYMENT_CANCELLED:
            case AccountState.CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE:
                return 'The request to pay transaction has been cancelled and can no longer be paid by the customer.';
            default:
                return 'Unknown';
        }
    }

    /**
     * @return the {@link getShortDescription} and {@link getLongDescription} of each account state, separated by a
     * new line.
     */
    public getDescriptionOfAllFormatted(): string
    {
        return this.PAYMENT_OUTSTANDING + ': ' +
            this.getLongDescription(AccountState.PAYMENT_OUTSTANDING) + '\n' +
            this.PROCESSING_PAYMENT + ': ' +
            this.getLongDescription(AccountState.PROCESSING_PAYMENT) + '\n' +
            this.PAYMENT_COMPLETED + ': ' +
            this.getLongDescription(AccountState.PAYMENT_COMPLETED) + '\n' +
            this.PAYMENT_READY_FOR_SETTLEMENT + ': ' +
            this.getLongDescription(AccountState.PAYMENT_READY_FOR_SETTLEMENT) + '\n' +
            this.PAYMENT_CANCELLED + ': ' +
            this.getLongDescription(AccountState.PAYMENT_CANCELLED) + '\n' +
            this.PAYMENT_EXPIRED + ': ' +
            this.getLongDescription(AccountState.PAYMENT_EXPIRED) + '\n' +
            this.CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE + ': ' +
            this.getLongDescription(AccountState.CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE);
    }

    public isPaymentOutstanding(accountState: AccountState): boolean
    {
        return accountState === AccountState.PAYMENT_OUTSTANDING;
    }

    /**
     * This method is used in combination with the style or ngStyle directive to display account states as different
     * colour text in an html element.
     *
     * @return the text colour that represents the account state, as a string.
     */

    public getAccountTextColorClass(accountState: AccountState): string
    {
        if (accountState == null)
        {
            return 'default-payment-color';
        }

        switch (accountState)
        {
            case AccountState.PAYMENT_CANCELLED:
            case AccountState.PAYMENT_EXPIRED:
            case AccountState.CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE:
                return 'expired-or-cancelled-payment-color';
            case AccountState.PROCESSING_PAYMENT:
            case AccountState.PAYMENT_COMPLETED:
            case AccountState.PAYMENT_FEES_ISSUE:
            case AccountState.PAYMENT_READY_FOR_SETTLEMENT:
            case AccountState.PARTIAL_PAYMENT_RECEIVED:
                return 'in-progress-payment-color';
            case AccountState.SETTLEMENT_PROCESSED:
                return 'settled-payment-color';
            default:
                return 'default-payment-color';
        }
    }
}